import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Typography
} from '@one-thd/sui-atomic-components';
import { retrieveEmailPreferences, unsubscribeEmail, updateEmailPreferences } from '../apis/email';
import { initialEmailTableData, RETARGETING_TYPE } from './tableData';
import MarketingPreferencesCard from './common/MarketingPreferencesCard';
import {
  convertSubsToPrefs,
  getSubscriptions, getUnsubscribeList,
  getUpdates,
  setupHandleEmailOrSmsTelemarketingRetrieve
} from '../apis/common';
import { BannerContext, BannerProvider } from '../contexts/BannerContext';
import { addAnalyticEvent } from '../analytics/common';
import {
  emailUpdateError,
  unsubscribeFromAllEmail, unsubscribeFromAllEmailError,
  unsubscribeFromAllEmailSuccess
} from '../analytics/email';
import ErrorBoundary from '../contexts/ErrorBoundary';
import { OptOutReasonFeedback } from './OptOutReasonFeedback';
import {
  OPTOUT_FEEDBACK_SUCCESS,
  OPTOUT_FEEDBACK_ERROR,
  ALL_EMAIL_UNSUBSCRIBE_SUCCESS
} from '../constants';
import { emailAnalyticEvent } from '../analytics/save';
import { RetrieveErrorContext } from '../contexts/RetrieveErrorContext';
import { determineMessage } from '../../utils/general';

export const unsubEmailsButton = 'email-unsub';
export const saveChangeButton = 'email-save-change';

export function Preferences({ email, isB2B }) {
  const {
    openErrorAlert, openSuccessAlert, closeSuccessAlert,
    selectedUndo
  } = React.useContext(BannerContext);

  const {
    addError
  } = React.useContext(RetrieveErrorContext);
  const [selected, setSelected] = React.useState([]);
  const [tableData, setTableData] = React.useState(initialEmailTableData());
  const [initialSubs, setInitialSubs] = React.useState([]);
  const handleRetrieve = setupHandleEmailOrSmsTelemarketingRetrieve(
    tableData, setTableData, setSelected, setInitialSubs
  );
  const [selectedOptOuts, setSelectedOptOuts] = React.useState([]);
  const initialSelection = selectedUndo;

  function handleSuccess(res) {
    let unsubscribe = getUnsubscribeList(selected, tableData);
    const messages = determineMessage(selected, tableData);

    openSuccessAlert(messages, email);
    setSelectedOptOuts(unsubscribe);
    handleRetrieve(res);
  }

  function handleError() {
    openErrorAlert();
  }

  function undoUnsubscribeFromAll(selectedPreferences) {
    updateEmailPreferences(email, selectedPreferences, null,
      (res) => {
        handleSuccess(res);
      }, (err) => {
        addAnalyticEvent(emailUpdateError);
        handleError(err);
      });
  }

  function resubPref() {
    closeSuccessAlert();
    undoUnsubscribeFromAll(initialSelection);
  }

  React.useEffect(() => {
    if (initialSelection && initialSelection.length > 0) {
      closeSuccessAlert();
      resubPref();
    }
  }, [selectedUndo]);

  function optOptReasonError() {
    openErrorAlert(OPTOUT_FEEDBACK_ERROR);
  }

  function handleRetrieveError() {
    addError();
  }

  React.useEffect(() => {
    retrieveEmailPreferences(email, handleRetrieve, handleRetrieveError);
  }, []);

  const clickSaveChanges = () => {
    if (selected.length) {
      closeSuccessAlert();
      updateEmailPreferences(email, selected, tableData,
        (res) => {
          addAnalyticEvent(emailAnalyticEvent(getUpdates(selected, tableData)));
          handleSuccess(res);
        }, (err) => {
          addAnalyticEvent(emailUpdateError);
          handleError(err);
        });
    }
  };

  const handleUnsubscribeFromAllClick = () => {
    closeSuccessAlert();
    addAnalyticEvent(unsubscribeFromAllEmail);
    unsubscribeEmail(email, (res) => {
      addAnalyticEvent(unsubscribeFromAllEmailSuccess);
      convertSubsToPrefs(tableData, getSubscriptions(res));
      openSuccessAlert(ALL_EMAIL_UNSUBSCRIBE_SUCCESS, email);
      setSelectedOptOuts([RETARGETING_TYPE]);
      handleRetrieve(res);
    }, (err) => {
      addAnalyticEvent(unsubscribeFromAllEmailError);
      handleError(err);
    });
  };

  function handleOptOutReasonSuccess() {
    setSelectedOptOuts([]);
    closeSuccessAlert();
    openSuccessAlert(OPTOUT_FEEDBACK_SUCCESS);
  }

  const callApi = (subscriptions) => {
    updateEmailPreferences(email, subscriptions, null,
      () => handleOptOutReasonSuccess(),
      () => {
        optOptReasonError();
        setSelectedOptOuts([]);
      });
  };
  const optOutB2BText = ' If you opt out of marketing emails you may still receive transactional communications.';
  return (
    <div className="sui-grid sui-gap-4">
      <OptOutReasonFeedback
        selectedOptOuts={selectedOptOuts}
        setSelectedOptOuts={setSelectedOptOuts}
        callApi={callApi}
      />
      <Typography variant="h2" weight="display">MARKETING EMAILS</Typography>
      <Typography variant="body-base" height="none">
        To make changes to your preferences, click on the individual preference options below.
        Please note that your consent to participate in the programs listed below will be treated{' '}
        separately from your consent to participate in any of our other programs.
        {isB2B && optOutB2BText}
      </Typography>
      {/* Marketing Table */}
      <MarketingPreferencesCard tableData={tableData} setTableData={setTableData} setSelected={setSelected} />
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
          <Button aria-label={saveChangeButton} fullWidth onClick={clickSaveChanges}>
            Save Changes
          </Button>
          <Button aria-label={unsubEmailsButton} fullWidth variant="secondary" onClick={handleUnsubscribeFromAllClick}>
            Opt Out From All Marketing Emails
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmailPreferences({ email, isB2B }) {

  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences email={email} isB2B={isB2B} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default EmailPreferences;

const proptypes = {
  email: PropTypes.string.isRequired,
  isB2B: PropTypes.bool
};
const defaultProps = {
  isB2B: false
};

EmailPreferences.propTypes = proptypes;
EmailPreferences.defaultProps = defaultProps;
Preferences.propTypes = proptypes;
Preferences.defaultProps = defaultProps;
